import { TEXT_BLOCKS, findInText, searchPattern, type SearchQuery } from '@realmery/realm-core';

/**
 * Highlighting a search's matches on an entry's page (docs/SEARCH-PLAN.md section 3.5).
 * Each scope's text is read a block at a time across its text nodes, as search reads it,
 * so a phrase with bold or a link in the middle is found and marked as one. The matches
 * are marked with the CSS Custom Highlight API, which adds nothing to the page's HTML;
 * a browser without it gets no marks, but still scrolls to the first match.
 */

export const HIGHLIGHT_NAME = 'search-match';

interface Piece {
  readonly node: Text;
  readonly start: number;
}

/** Every match in `scopes`, as ranges, in document order. */
export function findRanges(scopes: Iterable<Element>, query: SearchQuery): Range[] {
  const pattern = searchPattern(query);
  const ranges: Range[] = [];
  for (const scope of scopes) {
    const doc = scope.ownerDocument;
    let pieces: Piece[] = [];
    let text = '';
    let owner: Element | null = null;
    const flush = () => {
      for (const m of findInText(text, pattern)) {
        const range = doc.createRange();
        const at = (offset: number, isEnd: boolean) => {
          // The piece holding `offset` (for an end, the one it ends in).
          let piece = pieces[0]!;
          for (const p of pieces) {
            if (isEnd ? p.start < offset : p.start <= offset) piece = p;
          }
          return [piece.node, offset - piece.start] as const;
        };
        range.setStart(...at(m.start, false));
        range.setEnd(...at(m.end, true));
        ranges.push(range);
      }
      pieces = [];
      text = '';
      owner = null;
    };
    const blockOf = (node: Node): Element => {
      for (let el = node.parentElement; el && el !== scope; el = el.parentElement) {
        if (TEXT_BLOCKS.has(el.localName)) return el;
      }
      return scope;
    };
    const walker = doc.createTreeWalker(scope, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const name = (node as Element).localName;
        if (name === 'br' || TEXT_BLOCKS.has(name)) flush();
        continue;
      }
      const block = blockOf(node);
      if (owner !== null && owner !== block) flush();
      owner = block;
      const value = (node as Text).data;
      if (value === '') continue;
      pieces.push({ node: node as Text, start: text.length });
      text += value;
    }
    flush();
  }
  return ranges;
}

/** Marks `ranges` as the page's search highlight, replacing any before; returns a function that clears it. */
export function showHighlight(ranges: readonly Range[]): () => void {
  const registry = typeof CSS !== 'undefined' && 'highlights' in CSS ? CSS.highlights : null;
  if (!registry || typeof Highlight === 'undefined') return () => undefined;
  registry.set(HIGHLIGHT_NAME, new Highlight(...ranges));
  return () => registry.delete(HIGHLIGHT_NAME);
}
